import { Typography } from "@mui/material";
import { Box } from "@mui/material";
import { SYMPTOM_LABEL, Symptom } from "../../../models/Symptom";
import { UtcToLocale } from "../../../utility-components/UtcToLocale";
import { GOOGLE_MAPS_API_KEY } from "../../../App";
import { useGeoMap } from "../../../context/GeoMapContext";
import { useEffect, useRef } from "react";

const hostUrl = window.location.protocol.concat("//").concat(window.location.host);

interface SummaryStepProps {
  symptoms: Symptom[];
  remarksCitizen?: string;
  recognitionDateTime: Date;
}
export const SummaryStep = (props: SummaryStepProps) => {
  const { selectedMarker } = useGeoMap();
  const stepRef = useRef<HTMLDivElement>(null);

  // Scroll to top when this step mounts
  useEffect(() => {
    stepRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return !selectedMarker ? null : (
    <div ref={stepRef} style={{ width: '100%' }}>
      <Box display="flex" alignItems="center" gap="20px" flexWrap="wrap" justifyContent="center" width="100%" flexDirection={{ xs: 'column-reverse', sm: 'row' }}>
        <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Box display="flex" flexDirection="column" gap="15px">
            <Box display="flex" gap="10px" flexDirection={{ xs: 'column', sm: 'row' }}>
               <Typography variant="h6" sx={{ minWidth: { sm: 120 }, mb: { xs: 0.5, sm: 0 } }}> Mastnummer: </Typography>
            
              <Typography style={{ flex: 1 }}>{selectedMarker.number}</Typography>
            </Box>

            <Box display="flex" gap="10px" flexDirection={{ xs: 'column', sm: 'row' }}>
            <Typography variant="h6" sx={{ minWidth: { sm: 120 }, mb: { xs: 0.5, sm: 0 } }}> Adresse: </Typography>
              <Box style={{ flex: 1, padding:'3px' }}>
                {!!selectedMarker.street && (
                  <Typography>{selectedMarker.street + (selectedMarker.houseNumber ? " " + selectedMarker.houseNumber : "")}</Typography>
                )}
                {!!selectedMarker.city && (
                  <Typography>{(selectedMarker.district ? selectedMarker.district + ", " : "") + selectedMarker.city}</Typography>
                )}
                {!!selectedMarker.zipCode && <Typography>{selectedMarker.zipCode}</Typography>}
              </Box>
            </Box>
            <Box display="flex" gap="10px" flexDirection={{ xs: 'column', sm: 'row' }}>
            <Typography variant="h6" sx={{ minWidth: { sm: 120 }, mb: { xs: 0.5, sm: 0 } }}>Symptome:</Typography>
            
                <Box>
                  {props.symptoms.map((symptom) => (
                    <Typography style={{padding:'3px'}} key={symptom}>{SYMPTOM_LABEL[symptom]}</Typography>
                  ))}
                </Box>
            </Box>

            {props.remarksCitizen && (
              <Box display="flex" gap="10px" flexDirection={{ xs: 'column', sm: 'row' }}>
                  <Typography variant="h6" sx={{ minWidth: { sm: 120 }, mb: { xs: 0.5, sm: 0 } }}>Anmerkungen:</Typography>
                <Typography style={{ flex: 1, padding:'3px' }}>{props.remarksCitizen}</Typography>
              </Box>
            )}

            <Box display="flex" gap="10px" flexDirection={{ xs: 'column', sm: 'row' }}>
            <Typography variant="h6" sx={{ minWidth: { sm: 120 }, mb: { xs: 0.5, sm: 0 } }}>Erkennungszeit:</Typography>
              <Typography style={{ flex: 1, padding:'3px' }}>
                <UtcToLocale datetime={props.recognitionDateTime.toISOString()} />
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ width: { xs: '180px', sm: '250px' }, height: { xs: '180px', sm: '250px' }, mb: { xs: 2, sm: 0 } }}>
          <img
            src={renderStatic({
              marker: {
                latitude: selectedMarker?.latitude,
                longitude: selectedMarker?.longitude,
                icon: encodeURI(hostUrl + "/lightpoint-marker.png"),
              },
            })}
            alt="map-snapshot"
            style={{ borderRadius: "50%", objectFit: "cover", width: "100%", height: "100%" }}
          />
        </Box>
      </Box>
    </div>
  );
};

export default SummaryStep;

const renderStatic = (params: any) => {
  const defaultStaticRenderParams = new URLSearchParams([
    ["size", "400x400"],
    ["key", GOOGLE_MAPS_API_KEY],
    ["zoom", "18"],
  ]);

  const _params = new URLSearchParams(defaultStaticRenderParams);

  if (params.size) _params.set("size", params.size);
  if (params.zoom) _params.set("zoom", params.zoom);

  if (params.marker && params.marker.latitude && params.marker.longitude) {
    const markerLocation = `${params.marker.latitude},${params.marker.longitude}`;
    
    // Google Maps Static API requires styling parameters (like icon) to precede the location.
    // Also, if the environment is dev/localhost and is not publicly accessible to Google's servers, 
    // Google will silently drop the marker if it fails to fetch the custom icon URL.
    const isPublicEnv = !window.location.hostname.includes("localhost") && !window.location.hostname.includes("dev");
    
    let markerProps = markerLocation;
    if (params.marker.icon && isPublicEnv) {
      markerProps = `icon:${params.marker.icon}|${markerLocation}`;
    } else {
      // Fallback to a standard red marker on internal environments 
      // where Google can't fetch the localized lightpoint-marker.png
      markerProps = `color:red|${markerLocation}`;
    }
    
    _params.set("markers", markerProps);
  }

  return `https://maps.googleapis.com/maps/api/staticmap?${_params.toString()}`;
};
